import AppContext from "@/AppContext";
import { purchaseStartupPopup } from "@/Atoms";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useAuth from "@/hooks/auth";
import AccountLayout from "@/layouts/AccountLayout";
import {
  getAllAccessByVcId,
  getAllVcAccesses,
  getUserVcs,
  removeAccess,
  removeEditAccess,
  sendVcAccessInvitation,
} from "@/services/api/VentureCapital";
import { Check, Clock, Info, Trash } from "@phosphor-icons/react";
import { useAtom } from "jotai";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import Link from "next/link";
import {
  getBaseDomain,
  isValidDomain,
  isValidEmail,
  checkEmailDomainWithList as checkEmailDomain,
} from "@/utils/checkEmailDomain";
import Spinner from "@/components/Spinner";
import toast from "react-hot-toast";
import { Hourglass } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function AccessInvestor() {
  const { user } = useAuth({ middleware: "auth" });
  const [myVcs, setMyVcs] = useState([]);
  const [isVcOwner, setIsVcOwner] = useState(false);
  const [accesses, setAccesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const { state } = useContext(AppContext);
  const { planSubscription } = state;
  const router = useRouter();
  const { redirect_to } = router.query;
  const [openPurchaseStartupPopup, setOpenPurchaseStartupPopup] =
    useAtom(purchaseStartupPopup);

  const [isSameUserEmailAndWebsite, setIsSameUserEmailAndWebsite] =
    useState(false);
  const [memberEmail, setMemberEmail] = useState("");
  const [vcEditAccess, setVcEditAccess] = useState([]);

  const getVCEditAccess = async (vcId) => {
    try {
      const res = await getAllAccessByVcId(vcId, {
        params: {
          is_angel: false,
        },
      });
      if (res.data && res.data.data?.approved_members) {
        setVcEditAccess(res.data.data.approved_members);
        console.log(res.data);
      }
    } catch (error) {
      console.error("Error fetching VC edit access:", error);
    }
  };

  const getMyList = async () => {
    const response = await getUserVcs({
      params: {
        is_angel: false,
      },
    });
    if (response.data) {
      if (
        response.data.venture_capitals &&
        response.data.venture_capitals.length > 0
      ) {
        setMyVcs(response.data.venture_capitals);
        const responseVcAccess = await getAllVcAccesses(
          response.data.venture_capitals[0].id
        );
        getVCEditAccess(response.data.venture_capitals[0].id);
        if (responseVcAccess.data?.data) {
          setAccesses(responseVcAccess.data.data);
        }
      }
      setIsVcOwner(response.data.is_vc_owner);
    }
  };

  useEffect(() => {
    getMyList();
  }, []);

  const startRegisterStartup = () => {
    setOpenPurchaseStartupPopup(true);
  };

  const handleChange = async (event) => {
    if (myVcs.length > 0) {
      const email = event.target.value;
      setMemberEmail(email);
      const same = await checkEmailDomain(email, myVcs[0].website);
      setIsSameUserEmailAndWebsite(same);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (accesses.length === 3) {
      setLoading(false);
      return toast.error(
        "You have reached the limit of 3 members. You need to remove a member to add a new one!"
      );
    }

    if (accesses.find((access) => access.email == memberEmail)) {
      setLoading(false);
      return toast.error("Email is already on the list.");
    }

    if (memberEmail == user.email) {
      setLoading(false);
      return toast.error("You can not invite yourself.");
    }

    try {
      const response = await sendVcAccessInvitation({
        email: memberEmail,
        vc_id: myVcs[0].vc_id || myVcs[0].id,
      });
      setLoading(false);
      toast.success(`Successfully send the invitation to ${memberEmail}.`);
      getMyList();
      setMemberEmail("");
      setIsSameUserEmailAndWebsite(false);
    } catch (error) {
      console.log(error);
    }
  };

  // const continueClaim = async () => {
  //   if (planSubscription.session_detail.startup_id >= 0) {
  //     try {
  //       const res = await updateSession(planSubscription.session_detail.id, {
  //         startup_id: null,
  //       });
  //       location.href = "/startups/register?step=2";
  //     } catch (error) {
  //       router.push("/startups/register?step=2");
  //     }
  //   } else {
  //     router.push("/startups/register?step=2");
  //   }
  // };

  const continueClaim = async () => {
    router.push("/startups/register-new");
  };

  const handleRemoveAccess = async (id) => {
    try {
      const res = await removeAccess(id);
      getMyList();
    } catch (error) {
      console.log(error);
    }
  };

  const handleRemoveEditAccess = async (id) => {
    try {
      const res = await removeEditAccess(id);
      getMyList();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <AccountLayout title={"Access Investor | Lucidity Insights"}>
      <p className='text-2xl font-semibold'>Investor Profile Member Access</p>
      <p className='pb-5 mt-1.5 text-sm border-b text-white/40'>
        Manage who from your organization can manage your Investor Profile on
        Lucidity Insights.
      </p>
      {myVcs.length > 0 && (
        <div className='max-w-lg pt-4'>
          <p className='mb-3 text-sm opacity-50'>You have access to</p>
          {myVcs.map((vc) => (
            <div
              key={vc.vc_id || vc.id}
              className='flex items-center justify-between mb-2'
            >
              <div className='flex items-center space-x-3'>
                <Avatar>
                  <AvatarImage src={vc.logo_url} alt='' />
                  <AvatarFallback>{vc.name[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <p>{vc.name}</p>
                  <p className='text-xs text-white/40 mt-0.5'>{vc.website}</p>
                </div>
              </div>
              <Button className='border !border-white/5 !bg-white/5 text-white'>
                <Link
                  legacyBehavior
                  href={`/investors/${vc.slug}-${vc.vc_id || vc.id}`}
                >
                  <a target='_blank' rel='noopener noreferrer'>
                    Visit
                  </a>
                </Link>
              </Button>
            </div>
          ))}
        </div>
      )}
      {isVcOwner && (
        <div className='max-w-lg pt-5'>
          <Tabs defaultValue='members'>
            <TabsList>
              <TabsTrigger value='members'>Active Members</TabsTrigger>
              <TabsTrigger value='invitation'>Invite Members</TabsTrigger>
            </TabsList>
            <TabsContent value='invitation'>
              <div>
                <Label>Invite Members (Give Edit Access)</Label>
                <div className='flex items-center w-full mt-2 space-x-2'>
                  <Input
                    value={memberEmail}
                    type='email'
                    placeholder='Email'
                    onInput={handleChange}
                  />
                  <Button
                    title='tes'
                    disabled={!isSameUserEmailAndWebsite || loading}
                    onClick={handleSubmit}
                  >
                    {loading ? (
                      <>
                        <Spinner color='slate-800' /> Loading...
                      </>
                    ) : (
                      "Invite"
                    )}
                  </Button>
                </div>
                {memberEmail && (
                  <small className='text-[11px] text-red-400'>
                    {isSameUserEmailAndWebsite
                      ? ""
                      : `Email must ends with
                      @${getBaseDomain(myVcs[0]?.website)}`}
                  </small>
                )}
                <p className='flex mt-3 text-xs text-blue-500 rounded-md'>
                  <Info className='text-base mr-1.5' />
                  You can add up to three members from your organization to
                  access this VC
                </p>
              </div>
              <div className='mt-6'>
                <Label>Invited Members</Label>
                {accesses.length === 0 && (
                  <div className='mt-3 border text-center py-6 px-10 rounded-lg border-dashed'>
                    <p className='text-xs opacity-40'>
                      You have no invited members
                    </p>
                  </div>
                )}
                <div className='mt-4 space-y-6'>
                  {accesses.map((acc, index) => (
                    <div
                      key={acc.email + "-" + index}
                      className='flex items-center justify-between'
                    >
                      <div className='flex items-center space-x-3'>
                        <div>
                          <p className='text-sm lg:text-base text-white/80'>
                            {acc.email}
                          </p>
                          {!acc.status ? (
                            <div className='inline-flex items-center mt-1.5 text-xs font-medium text-yellow-600'>
                              <span className='inline-flex items-center justify-center border border-yellow-50/10 w-5 h-5 bg-yellow-100/10 rounded-full mr-1.5'>
                                <Hourglass className='w-2.5 h-2.5' />
                              </span>
                              Pending
                            </div>
                          ) : (
                            <div className='inline-flex items-center mt-1.5 text-xs font-medium text-green-600'>
                              <span className='inline-flex items-center justify-center border border-green-50/10 w-5 h-5 bg-green-100/10 rounded-full mr-1.5'>
                                <Check className='w-2.5 h-2.5' />
                              </span>
                              Granted
                            </div>
                          )}
                        </div>
                      </div>
                      {!acc.status ? (
                        <Button
                          className='h-auto px-3 py-2 text-xs border !border-white/5 !bg-white/5 text-white'
                          disabled
                        >
                          <Clock className='w-3.5 h-3.5 mr-1.5' />
                          Waiting for confirmation...
                        </Button>
                      ) : (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button className='text-xs py-2 px-3 h-auto !bg-red-400/10 text-red-600 hover:!bg-red-400/20'>
                              <Trash className='w-3.5 h-3.5 mr-1.5' />
                              Remove Access
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className='bg-white text-background'>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                You sure to remove access from this account?
                              </AlertDialogTitle>
                              <AlertDialogDescription className='text-background/60'>
                                They will be notified via email if they lost
                                access to this VC. This action cannot be undone.
                                You can re-add the email later on.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className='border-background/10 hover:bg-background/5 bg-white/5'>
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleRemoveAccess(acc.id)}
                                className='text-white bg-background hover:bg-background/80'
                              >
                                Delete Access
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
            <TabsContent value='members'>
              <div className='mt-6'>
                <Label>Members with edit access</Label>
                {vcEditAccess.length === 0 && (
                  <div className='mt-3 border text-center py-6 px-10 rounded-lg border-dashed'>
                    <p className='text-xs opacity-40'>
                      You have no members with edit access
                    </p>
                  </div>
                )}
                <div className='mt-4 space-y-6'>
                  {vcEditAccess.map((acc, index) => (
                    <div
                      key={acc.email + "-" + index}
                      className='flex items-center justify-between'
                    >
                      <div className='flex items-center space-x-3'>
                        <div>
                          <p className='text-sm lg:text-base text-white/80'>
                            {acc.first_name} {acc.last_name}{" "}
                            {acc.role && (
                              <span className='text-xs text-white/40'>
                                ({acc.role})
                              </span>
                            )}
                          </p>
                          <p className='text-sm lg:text-base text-white/80'>
                            {acc.email}
                          </p>
                        </div>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button className='text-xs py-2 px-3 h-auto !bg-red-400/10 text-red-600 hover:!bg-red-400/20'>
                            <Trash className='w-3.5 h-3.5 mr-1.5' />
                            Remove Access
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className='bg-white text-background'>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              You sure to remove access from this account?
                            </AlertDialogTitle>
                            <AlertDialogDescription className='text-background/60'>
                              They will be notified via email if they lost
                              access to this VC. This action cannot be undone.
                              You can re-add the email later on.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className='border-background/10 hover:bg-background/5 bg-white/5'>
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRemoveEditAccess(acc.vc_member_id)}
                              className='text-white bg-background hover:bg-background/80'
                            >
                              Delete Access
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </AccountLayout>
  );
}
